'use client'
import { useComponentTranslation } from '@core/i18n'
import { Box, Flex, Icon, Tag, Text, Tooltip } from '@core/ui-elements'
import { IPickingProduct } from '@data/types'
import copy from 'copy-to-clipboard'
import { FC, useState } from 'react'
import { AiFillFlag } from 'react-icons/ai'
import { MdCircle, MdContentCopy } from 'react-icons/md'

import { CounterTag } from '../counter-tag'
import { FallbackImageError } from '../fallback-image-error'
export interface IDeliveryDetailProductCard {
  product: IPickingProduct
  isCurrentProduct?: boolean
}

export const DeliveryDetailProductCard: FC<IDeliveryDetailProductCard> = ({
  product,
  isCurrentProduct = false,
}) => {
  const t = useComponentTranslation('DeliveryDetailProductCard')
  const [tooltipMsg, setTooltipMsg] = useState('Copiar código')

  const handlerCopyBarCode = (code: string) => {
    copy(code)
    setTooltipMsg(t('Code copied!'))
  }

  const cardColor = {
    currentProduct: {
      backgroundColor: 'orange.100',
      borderColor: 'yellow.500',
    },
    flaggedProduct: {
      backgroundColor: 'red.50',
      borderColor: 'red.500',
    },
  }

  const productType = product?.flaggedAmount
    ? 'flaggedProduct'
    : isCurrentProduct
      ? 'currentProduct'
      : undefined

  return (
    <Flex
      alignItems="center"
      backgroundColor={productType && cardColor[productType].backgroundColor}
      borderBottom="1px solid"
      borderColor="gray.200"
      borderInline="3rem"
      borderRadius="0.5rem"
      data-testid={'delivery-detail-product-card'}
      gap="0.5rem"
      justifyContent="space-between"
      padding="0.5rem 0"
      sx={{
        ':last-child': {
          borderBottom: 'none',
        },
      }}
      width="100%"
    >
      <Flex
        align={'center'}
        borderLeft={productType ? '2px solid' : undefined}
        borderLeftColor={productType && cardColor[productType].borderColor}
        gap={'0.25rem'}
        overflow={'hidden'}
        paddingLeft="0.25rem"
        width="100%"
      >
        {product.imageUrl && (
          <Flex
            height={'3.75rem'}
            justifyContent="center"
            margin="0"
            minWidth={'3.75rem'}
            paddingLeft="0.25rem"
          >
            <FallbackImageError
              alt={product.name || ''}
              imageUrl={product.imageUrl}
              size="2.25rem"
            />
          </Flex>
        )}
        <Box overflow="hidden" width="100%">
          <Text
            fontWeight={'600'}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            translate="no"
            whiteSpace={'nowrap'}
            width="100%"
          >
            {product.name}
          </Text>
          <Text color={'gray.600'} fontSize={'0.75rem'} fontWeight={'normal'}>
            {t('Code', {
              code: product.barcodes[0],
            })}
            <Tooltip
              hasArrow
              aria-label="Copiar código"
              closeOnClick={false}
              label={tooltipMsg}
              placement="top"
              shouldWrapChildren={true}
              onClose={() => setTooltipMsg(t('Copy code'))}
            >
              <Icon
                as={MdContentCopy}
                cursor={'pointer'}
                marginLeft="0.5rem"
                onClick={() => handlerCopyBarCode(product.barcodes[0])}
              />
            </Tooltip>
          </Text>
        </Box>
      </Flex>
      <Flex
        alignItems="center"
        gap={'0.25rem'}
        justifyContent={'right'}
        paddingRight="0.25rem"
        whiteSpace="nowrap"
        wrap={'wrap'}
      >
        {!product.scannedAmount &&
          !product.manuallyAmount &&
          !product.flaggedAmount &&
          !isCurrentProduct && (
            <Tag backgroundColor={'white'}>
              {t('itemsToPick', { count: product.amount })}
            </Tag>
          )}
        {!product.scannedAmount &&
          !product.manuallyAmount &&
          !product.flaggedAmount &&
          isCurrentProduct && (
            <Tag backgroundColor={'white'}>
              <Icon
                as={MdCircle}
                boxSize={'0.5rem'}
                color={'yellow.500'}
                marginRight={'0.25rem'}
              />
              {t('Collection in progress')}
            </Tag>
          )}
        {!!product.scannedAmount && (
          <CounterTag
            amount={product.scannedAmount}
            color={product.isCompleted ? 'green.500' : 'red.500'}
            icon={MdCircle}
            label={t('scanned', { count: product.amount })}
            total={product.amount}
          />
        )}
        {!!product.manuallyAmount && (
          <CounterTag
            amount={product.manuallyAmount}
            color={product.isCompleted ? 'blue.500' : 'red.500'}
            icon={MdCircle}
            label={t('manual', { count: product.amount })}
            total={product.amount}
          />
        )}
        {!!product.flaggedAmount && (
          <CounterTag
            amount={product.flaggedAmount}
            amountWithColor={true}
            color={'red.500'}
            icon={AiFillFlag}
            label={t('flagged', { count: product.amount })}
            total={product.amount}
          />
        )}
      </Flex>
    </Flex>
  )
}
